
import { RockAnalysis, Language } from "../types";
import { ROCK_SCHEMA, THIN_SECTION_LOGIC } from "./knowledge_base";
import { UNIVERSAL_LOGIC } from "./knowledge_base/universal_logic";

// ==========================================
// 🚀 核心鉴定函数 (Core Identification)
// ==========================================
export const identifyRock = async (
  images: { base64: string; mimeType: string; label?: string }[],
  lang: Language = 'zh',
  diagnosticFeedback?: Record<string, string>,
  userId?: string
): Promise<RockAnalysis> => {
    const langInstruction = lang === 'en' 
      ? "CRITICAL: You MUST provide all text values in English. Maintain professional geological terminology."
      : "核心要求：你必须使用中文提供所有文本值。保持专业的地质学术语。";

    // 🧠 通用且强大的结构化思维流程 (Universal Chain of Thought)
    const systemInstruction = `
${UNIVERSAL_LOGIC.roleDefinition}

${langInstruction}

${UNIVERSAL_LOGIC.universalThinkingChain}

=== 1. 多图综合策略 (Multi-image Strategy) ===
- 将多张图片视为同一标本的不同角度或不同光照条件。必须进行交叉验证。
- 对于手标本与薄片图共有的情况，请确保宏观推断与微观光学特征（如干涉色、突起、结构）结论互相印证。

=== 2. 显微偏光专精 (Optical Mineralogy & Thin Section Focus) ===
(仅在输入包含偏光显微镜图片时优先参考)
${THIN_SECTION_LOGIC.opticalRules}

=== 3. 鉴定防敷衍与规范纪律 (CRITICAL SCHEMA COMPLIANCE) ===
- **第二可能性 (secondPossibility)**: 如果鉴定存疑，通过结构化的逻辑必须提供替代方案，禁止留空，解释你为何排除了第一方案而考虑它。
- **化石防偏红线 (fossilAnalysis)**: 绝对不可给纯无机岩石或假化石（如树枝石、石英条带玛瑙）输出化石报告，极度严格要求返回 null。只有解剖学意义上的明确特征才可鉴定。
- **无废话原则**: geologicAge / environment / preservation 等字段禁止输出“待分析”、“详情见描述”等占位词。必须结合你的推断给出专业判断。
- **经济与找矿特征**: 必须客观严谨（字数>100字），若无直接矿体，评价其围岩、地质流体线索或普通石灰、建筑材料价值，各个子维度绝对不留空！
- **交互诊断**: 请给出2个在当前图片判断下，最具排他性的物理/化学测试建议（如硬度、盐酸试验等）。
`;

    try {
      // Create an abort controller with a relatively long timeout (e.g. 180 seconds)
      // and implement a retry mechanism specifically for network failures
      const fetchWithRetry = async (url: string, options: RequestInit, maxRetries = 2): Promise<Response> => {
        for (let i = 0; i < maxRetries; i++) {
          try {
            const controller = new AbortController();
            // Increased to 180s (3 mins) because advanced rock diagnosis takes time
            const timeoutId = setTimeout(() => controller.abort(), 180000); 
            
            const res = await fetch(url, { ...options, signal: controller.signal });
            clearTimeout(timeoutId);
            return res;
          } catch (err) {
            const errString = String(err).toLowerCase();
            // Only retry on network errors or timeouts, NOT on server 4xx/5xx responses
            if (i < maxRetries - 1 && (errString.includes('failed to fetch') || errString.includes('network error') || errString.includes('abort'))) {
              console.warn(`Network glitch detected. Retrying request (${i + 1}/${maxRetries})...`);
              // Wait 2 seconds before retrying
              await new Promise(resolve => setTimeout(resolve, 2000));
              continue;
            }
            // Enhance the abort error message for UI
            if (errString.includes('abort')) {
              throw new Error('鉴定耗时过长，网络连接已断开，请稍后重试。');
            }
            throw err;
          }
        }
        throw new Error('Max retries reached');
      };

      const response = await fetchWithRetry('/api/identify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          images,
          lang,
          systemInstruction: systemInstruction + "\n\nCRITICAL: You MUST output fields in the specified order.",
          responseSchema: ROCK_SCHEMA,
          diagnosticFeedback,
          userId
        })
      });

      if (!response.ok) {
        let errorMsg = `Server Error (${response.status})`;
        try {
          const errorData = await response.json();
          errorMsg = errorData.error || errorMsg;
        } catch {
          // If not JSON, try to get text or just use status
          const text = await response.text().catch(() => "");
          if (text.includes("<!doctype html>") || text.includes("<html>")) {
            errorMsg = "服务器暂时不可用 (502/504) 或图片体积过大被拦截，请稍后重试。";
          } else if (text) {
            errorMsg = text.slice(0, 100);
          }
        }
        throw new Error(errorMsg);
      }

      const result = await response.json();
      const text = result.text || "";

      if (!text) throw new Error("AI 鉴定未能生成有效文本。");

      // 尝试解析并修复可能的截断 JSON
      let parsedData;
      try {
        parsedData = JSON.parse(text);
      } catch {
        try {
          const repairedText = tryRepairJson(text);
          parsedData = JSON.parse(repairedText);
          console.warn("JSON was repaired successfully.");
        } catch {
          console.error("JSON Parse Error. Raw text:", text);
          throw new Error("AI 返回的格式不正确，解析失败。");
        }
      }
      
      // 深度兼容：合并可能被包裹在子 key 中的数据
      let finalData = { ...parsedData };
      const wrapperKeys = ['rock_analysis', 'analysis', 'data', 'result', 'rockAnalysis', 'rock'];
      
      for (const key of wrapperKeys) {
        if (parsedData[key] && typeof parsedData[key] === 'object') {
          finalData = { ...finalData, ...parsedData[key] };
        }
      }
      
      // 深度兼容：递归查找任何对象，如果它包含 'name' 和 'category'，则认为它是核心数据
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const findCoreData = (obj: any): any => {
        if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return null;
        // 增加对多种字段名的兼容，防止 AI 幻觉
        const nameVal = obj.name || obj.rockName || obj.title || obj.identification;
        const catVal = obj.category || obj.type || obj.classification;
        
        if (nameVal && catVal && (obj.detailedDescription || obj.formationReason)) {
          return { ...obj, name: nameVal, category: catVal };
        }
        
        for (const key of Object.keys(obj)) {
          const found = findCoreData(obj[key]);
          if (found) return found;
        }
        return null;
      };

      const extractedCore = findCoreData(parsedData);
      if (extractedCore) {
        finalData = { ...finalData, ...extractedCore };
      }

      // 如果还是找不到核心字段，尝试在顶级字段中寻找兼容名
      if (!finalData.name) {
        finalData.name = parsedData.rockName || parsedData.title || parsedData.identification;
      }
      if (!finalData.category) {
        finalData.category = parsedData.rockCategory || parsedData.type || parsedData.classification;
      }

      // 确保字段完整性，防止 UI 崩溃
      return ensureAllFields(finalData);
    } catch (err) {
      console.error("Identification failed:", err);
      throw err;
    }
};

/**
 * 尝试修复截断或不完整的 JSON 字符串
 */
const tryRepairJson = (json: string): string => {
  let repaired = json.trim();
  
  // 清理可能存在的 Markdown 代码块包裹
  repaired = repaired.replace(/^```json\n?/, '').replace(/\n?```$/, '').trim();

  // 如果已经闭合，直接返回
  if (repaired.endsWith('}')) return repaired;

  // 1. 处理截断在字符串内部的情况
  let quoteCount = 0;
  for (let i = 0; i < repaired.length; i++) {
    if (repaired[i] === '"' && (i === 0 || repaired[i-1] !== '\\')) {
      quoteCount++;
    }
  }
  if (quoteCount % 2 !== 0) {
    repaired += '"';
  }

  // 2. 移除末尾可能的逗号或未完成的键值对
  repaired = repaired.replace(/,\s*"[^"]*"\s*:\s*[^,}]*$/, '');
  repaired = repaired.replace(/,\s*"[^"]*"\s*$/, '');
  repaired = repaired.replace(/,\s*$/, '');

  // 3. 补全缺失的括号
  const stack: string[] = [];
  for (let i = 0; i < repaired.length; i++) {
    const char = repaired[i];
    if (char === '{') stack.push('}');
    else if (char === '[') stack.push(']');
    else if (char === '}' || char === ']') {
      if (stack.length > 0 && stack[stack.length - 1] === char) {
        stack.pop();
      }
    }
  }

  while (stack.length > 0) {
    repaired += stack.pop();
  }

  return repaired;
};

/**
 * 确保返回的对象包含所有必需的字段，防止 UI 崩溃
 */
const ensureAllFields = (data: Record<string, unknown>): RockAnalysis => {
  const defaults: RockAnalysis = {
    name: "鉴定结果 (部分)",
    englishName: "Identification Result (Partial)",
    category: "分析中...",
    isThinSection: false,
    grainSize: "",
    textureAndStructure: "",
    minerals: [],
    hardness: "",
    weathering: "",
    colorCause: "",
    alteration: "",
    metamorphism: "",
    formationReason: "",
    geologicalContext: "",
    detailedDescription: "",
    confidenceScore: 0,
    diagnosticTests: [],
    secondPossibility: null,
    economicAnalysis: {
      alterationMapping: "",
      mineralIndicators: "",
      structuralControl: "",
      prospectingPotential: ""
    },
    microscopicAnalysis: {
      texture: "",
      xplFeatures: "",
      pplFeatures: "",
      inference: ""
    },
    fossilAnalysis: null
  };

  // 深度合并
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const result: any = { ...defaults, ...data };

  // 矿物字段兼容处理：确保是数组
  if (data.minerals) {
    if (typeof data.minerals === 'string') {
      result.minerals = (data.minerals as string).split(/[，,、\s]+/).filter(Boolean);
    } else if (Array.isArray(data.minerals)) {
      result.minerals = data.minerals.map(m => String(m));
    }
  }

  // ==== 修复经济地质和微观分析的强制渲染文本 ====
  const rawEco = result.economicAnalysis || {};
  result.economicAnalysis = { 
    alterationMapping: rawEco.alterationMapping || "尚未发现明显的鉴别性蚀变特征，或需要进一步采样分析。",
    mineralIndicators: rawEco.mineralIndicators || "无特殊指示矿物，可结合区域地质资料辅助判断。",
    structuralControl: rawEco.structuralControl || "样本层面未见典型的控矿构造痕迹。",
    prospectingPotential: rawEco.prospectingPotential || "该样本所属岩相在周边可能具有一定勘探意义，需结合化探数据综合评价。"
  };
  
  const rawMicro = result.microscopicAnalysis || {};
  result.microscopicAnalysis = { 
    texture: rawMicro.texture || "常规显微结构特征。",
    xplFeatures: rawMicro.xplFeatures || "正交偏光下特征不明显或未做特殊记录。",
    pplFeatures: rawMicro.pplFeatures || "单偏光下特征见正文描述。",
    inference: rawMicro.inference || "微观推断与宏观特征基本一致。"
  };

  // 清理无效的诊断测试
  if (Array.isArray(result.diagnosticTests)) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    result.diagnosticTests = result.diagnosticTests.filter((t: any) => t && typeof t === 'object' && t.test && t.test.trim() !== '');
  }
  
  if (!result.diagnosticTests || result.diagnosticTests.length === 0) {
    result.diagnosticTests = [
      {
        test: "硬度测试 (Hardness Test)",
        instruction: "尝试用指甲、铜币或钢刀刻划样本新鲜面。",
        expected: "记录是否能留下划痕，以评估摩氏硬度级别。"
      },
      {
        test: "光泽与条痕 (Luster & Streak)",
        instruction: "在自然光下观察反光情况，并在无釉白瓷板上用力划出粉末（条痕）。",
        expected: "记录条痕的颜色及光泽类型（如金属光泽、玻璃光泽等）。"
      }
    ];
  }

  // 严谨判断是否为化石，防止空对象 {} 误判
  const hasFossilCategory = result.category && typeof result.category === 'string' && result.category.includes('化石');
  const hasFossilName = result.name && typeof result.name === 'string' && (result.name.includes('化石') || (result.englishName && result.englishName.toLowerCase().includes('fossil')));
  const hasFossilData = data.fossilAnalysis && typeof data.fossilAnalysis === 'object' && Object.values(data.fossilAnalysis).some(val => typeof val === 'string' && val.trim() !== '');

  if (hasFossilCategory || hasFossilName || hasFossilData) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const rawFossil: any = data.fossilAnalysis || {};
    result.fossilAnalysis = { 
      fossilType: rawFossil.fossilType || "古生物化石", 
      genusSpecies: rawFossil.genusSpecies || "待定 (Genus species pending)", 
      geologicalAge: rawFossil.geologicalAge || "待分析", 
      preservation: rawFossil.preservation || "见图", 
      environment: rawFossil.environment || "沉积环境", 
      description: rawFossil.description || "检测到化石特征，请结合地层背景进一步分析。"
    };
  } else {
    result.fossilAnalysis = null;
  }
  
  return result as RockAnalysis;
};

// v7.0.1: 移除生图功能 (Removed generateThinSectionImage)